import { Slider } from "@material-ui/core";
import PropTypes from "prop-types";
import * as React from "react";
import { useDebounce } from "../../../hooks";
import { currencyFormatter } from "../../../util/formatCurrency";
import { MAX_PRICE } from "../utils";
import s from "./PriceRangeFilter.module.css";

const floor = (n) => Math.floor(n / 100) * 100;
const ceil = (n) => Math.ceil(n / 100) * 100;

export default function PriceRangeFilter({ filter, setFilterAndRestoreRank }) {
  const [price, setPrice] = React.useState([
    filter.minPrice,
    filter.maxPrice > 0 ? filter.maxPrice : MAX_PRICE,
  ]);

  const [minPrice, setMinPrice] = React.useState(String(filter.minPrice / 100));
  const [maxPrice, setMaxPrice] = React.useState(
    filter.maxPrice === 0
      ? `${MAX_PRICE / 100}+`
      : String(filter.maxPrice / 100),
  );

  useDebounce(
    () => {
      const min = price[0];
      const max = price[1] >= MAX_PRICE ? 0 : price[1];

      if (min === filter.minPrice && max === filter.maxPrice) {
        return;
      }

      setFilterAndRestoreRank((prev) => ({
        ...prev,
        minPrice: min,
        maxPrice: max,
      }));
    },
    250,
    [filter, price],
  );

  return (
    <div className={s.section}>
      <p className={s.label} id="price-slider">
        Price
      </p>

      <div className={s.inputs}>
        <div className={s.inputWrap}>
          <span className={s.inputPrefix}>$</span>
          <input
            className={s.input}
            type="text"
            inputMode="numeric"
            aria-label="Min Price"
            value={minPrice}
            onChange={(e) => {
              const raw = e.target.value.trim().replace(/\D/g, "");

              if (!raw) {
                setMinPrice(e.target.value);
                return;
              }

              const parsedValue = Number(raw) * 100;

              if (Number.isNaN(parsedValue)) {
                setMinPrice(e.target.value);
                return;
              }

              const boundedValue = Math.max(0, parsedValue);
              setMinPrice(String(boundedValue / 100));
              setPrice(([, max]) => [floor(boundedValue), max]);
            }}
          />
        </div>

        <div className={s.inputWrap}>
          <span className={s.inputPrefix}>$</span>
          <input
            className={s.input}
            type="text"
            inputMode="numeric"
            aria-label="Max Price"
            value={maxPrice}
            onChange={(e) => {
              const raw = e.target.value.trim().replace(/\D/g, "");

              if (!raw) {
                setMaxPrice(e.target.value);
                return;
              }

              const parsedValue = Number(raw) * 100;

              if (Number.isNaN(parsedValue)) {
                setMaxPrice(e.target.value);
                return;
              }

              let boundedValue = Math.max(0, parsedValue);

              if (boundedValue > MAX_PRICE) {
                setMaxPrice(`${MAX_PRICE / 100}+`);
                boundedValue = MAX_PRICE;
              } else {
                setMaxPrice(String(boundedValue / 100));
              }

              setPrice(([min]) => [min, ceil(boundedValue)]);
            }}
            onBlur={(e) => {
              const parsedValue = Number(e.target.value.trim()) * 100;
              if (!Number.isNaN(parsedValue) && parsedValue > MAX_PRICE) {
                setMaxPrice(`${MAX_PRICE / 100}+`);
              }
            }}
          />
        </div>
      </div>

      <div className={s.slider}>
        <Slider
          aria-labelledby="price-slider"
          getAriaValueText={(value) => currencyFormatter.format(value / 100)}
          max={MAX_PRICE}
          min={0}
          step={1}
          valueLabelDisplay="off"
          value={price}
          onChange={(_, newValue) => {
            const [min, max] = newValue;
            setPrice([floor(min), ceil(max)]);
            setMinPrice(String(floor(min) / 100));
            setMaxPrice(
              max >= MAX_PRICE
                ? `${MAX_PRICE / 100}+`
                : String(ceil(max) / 100),
            );
          }}
        />
      </div>
    </div>
  );
}

PriceRangeFilter.propTypes = {
  filter: PropTypes.shape({
    minPrice: PropTypes.number.isRequired,
    maxPrice: PropTypes.number.isRequired,
  }).isRequired,
  setFilterAndRestoreRank: PropTypes.func.isRequired,
};
